// StrategyHeader — unified header block for all strategy tabs
// Renders: title, description, rules, KPI cards, capital breakdown, remarks

import React from 'react';
import { fmtRs, fmtRsSimple, fmtPct, pnlColor, computeCapitalMetrics, generateRemarks, STRATEGY_CONFIG } from './dashboardUtils';

// Card component for KPI display
function KPICard({ label, primary, secondary, color }) {
  return (
    <div style={{
      background: 'var(--bg1)',
      border: '1px solid var(--bgray)',
      borderRadius: 6,
      padding: '10px 12px',
      minWidth: 120,
      flex: 1,
    }}>
      <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color || 'var(--t1)' }}>
        {primary}
      </div>
      {secondary && (
        <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 2 }}>
          {secondary}
        </div>
      )}
    </div>
  );
}

// Capital progress bar
function CapitalBar({ deployed, total, pct }) {
  return (
    <div style={{ margin: '8px 0' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        fontSize: 11, 
        color: 'var(--t2)', 
        marginBottom: 4 
      }}>
        <span>Capital Deployed: {fmtRsSimple(deployed)} / {fmtRsSimple(total)}</span>
        <span>{pct.toFixed(0)}%</span>
      </div>
      <div style={{
        height: 6,
        background: 'var(--bg2)',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`,
          height: '100%',
          background: pct > 90 ? '#c92a2a' : pct > 70 ? '#f59f00' : '#0d7f3f',
          transition: 'width 0.3s',
        }} />
      </div>
    </div>
  );
}

export default function StrategyHeader({ strategyKey, lifetime, todayClosed, openPositions, mode = 'PAPER' }) {
  const cfg = STRATEGY_CONFIG[strategyKey];
  if (!cfg) return <div>Unknown strategy: {strategyKey}</div>;

  const capital = computeCapitalMetrics(strategyKey, openPositions);
  const remarks = generateRemarks({ lifetime, todayClosed, openPositions, strategyKey });

  // Compute metrics
  const lifetimeNet = lifetime?.lifetime_realized_net ?? lifetime?.realized_pnl_net ?? 0;
  const todayNet = (todayClosed || []).reduce((s, p) => s + Number(p.net_pnl || 0), 0);
  const todayCount = (todayClosed || []).length;
  const todayWins = (todayClosed || []).filter(p => Number(p.net_pnl || 0) > 0).length;
  const todayLosses = todayCount - todayWins;
  const todayWR = todayCount > 0 ? (todayWins / todayCount) * 100 : 0;
  const openUnrealized = lifetime?.current_unrealized_net ?? null;
  const totalUnrealized = lifetime?.total_unrealized_net ?? lifetime?.current_unrealized_net ?? null;
  const todayUnrealized = lifetime?.today_unrealized_net ?? null;
  const openCount = capital?.slotsUsed ?? 0;
  const wr = lifetime?.win_rate_pct ?? 0;
  const wins = lifetime?.wins_lifetime ?? lifetime?.wins ?? 0;
  const losses = lifetime?.losses_lifetime ?? ((lifetime?.closed_count || lifetime?.closed_positions_lifetime || 0) - wins);
  const totalClosed = lifetime?.closed_trades_lifetime ?? lifetime?.closed_count ?? lifetime?.closed_positions_lifetime ?? 0;

  return (
    <div style={{
      background: 'var(--bg0)',
      border: '1px solid var(--bgray)',
      borderRadius: 8,
      padding: 14,
      marginBottom: 12,
    }}>
      {/* Title Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)' }}>
          {cfg.name}
        </div>
        <div style={{
          fontSize: 10,
          padding: '2px 8px',
          borderRadius: 4,
          background: mode === 'LIVE' ? '#c92a2a' : '#f59f00',
          color: '#fff',
          fontWeight: 600,
        }}>
          {mode} MODE
        </div>
      </div>

      {/* Description */}
      <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 6 }}>
        {cfg.description}
      </div>

      {/* Rules */}
      <div style={{
        fontSize: 10,
        color: 'var(--t2)',
        marginBottom: 12,
        padding: '4px 8px',
        background: 'var(--bg1)',
        borderRadius: 4,
        fontFamily: 'monospace',
      }}>
        Rules: {cfg.rules}
      </div>

      {/* KPI Row — 5 cards. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <KPICard
          label="Lifetime Realized"
          primary={fmtRs(lifetimeNet)}
          secondary={`${totalClosed} closed`}
          color={pnlColor(lifetimeNet)}
        />
        <KPICard
          label="Total Unrealized"
          primary={totalUnrealized !== null ? fmtRs(totalUnrealized) : '—'}
          secondary={`${openCount} open (all)`}
          color={totalUnrealized !== null ? pnlColor(totalUnrealized) : undefined}
        />
        <KPICard
          label="Today Unrealized"
          primary={todayUnrealized !== null ? fmtRs(todayUnrealized) : '—'}
          secondary="entries today only"
          color={todayUnrealized !== null ? pnlColor(todayUnrealized) : undefined}
        />
        <KPICard
          label="Today Realized"
          primary={fmtRs(todayNet)}
          secondary={`${todayCount} closed today`}
          color={pnlColor(todayNet)}
        />
        <KPICard
          label="Today Win Rate"
          primary={todayCount > 0 ? `${todayWR.toFixed(1)}%` : '—'}
          secondary={todayCount > 0 ? `${todayWins}W / ${todayLosses}L today` : 'no closes yet'}
          color={todayCount > 0 && todayWR >= 50 ? '#0d7f3f' : (todayCount > 0 ? '#c92a2a' : undefined)}
        />
      </div>

      {/* Capital block */}
      {capital && (
        <>
          <CapitalBar 
            deployed={capital.deployed} 
            total={capital.totalCapital} 
            pct={capital.deployedPct} 
          />
          <div style={{ 
            fontSize: 11, 
            color: 'var(--t2)', 
            display: 'flex', 
            gap: 16, 
            flexWrap: 'wrap',
            marginBottom: 8,
          }}>
            <span>Slots: <b>{capital.slotsUsed}/{capital.maxSlots}</b></span>
            <span>Per slot: <b>{fmtRsSimple(capital.perSlot)}</b></span>
            <span>Available: <b>{fmtRsSimple(capital.available)}</b></span>
          </div>
        </>
      )}

      {/* Remarks */}
      {(remarks.working.length > 0 || remarks.watch.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
          {remarks.working.map((r, i) => (
            <div key={`w${i}`} style={{ fontSize: 11, color: '#0d7f3f' }}>
              💡 {r}
            </div>
          ))}
          {remarks.watch.map((r, i) => (
            <div key={`x${i}`} style={{ fontSize: 11, color: '#c92a2a' }}>
              ⚠️ {r}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
